/**
 * Utility functions for Rig Calibration module
 */

import { BUCKET_THRESHOLDS, BUCKET_NAMES, BUCKET_COLORS, VALIDATION_LIMITS, PERCENTAGE_LIMITS } from './constants';

export interface BucketInfo {
  name: string;
  color: string;
}

type ApiErrorShape = {
  message?: string;
  response?: {
    status?: number;
    data?: {
      detail?: string;
    };
  };
};

function asApiError(error: unknown): ApiErrorShape {
  return typeof error === 'object' && error !== null
    ? error as ApiErrorShape
    : {};
}

/**
 * Determine bucket type based on mud density
 */
export function getBucketByDensity(
  mudDensity: number | null | undefined
): BucketInfo | null {
  if (mudDensity === null || mudDensity === undefined) {
    return null;
  }

  if (mudDensity <= BUCKET_THRESHOLDS.LOW_MAX) {
    return { name: BUCKET_NAMES.LOW, color: BUCKET_COLORS.LOW };
  } else if (mudDensity < BUCKET_THRESHOLDS.MID_MAX) {
    return { name: BUCKET_NAMES.MID, color: BUCKET_COLORS.MID };
  } else {
    return { name: BUCKET_NAMES.HIGH, color: BUCKET_COLORS.HIGH };
  }
}

/**
 * Format bucket name for display
 */
export function formatBucketName(bucketPrefix: string): string {
  const bucketNames: Record<string, string> = {
    low_bucket: 'Low Bucket',
    mid_bucket: 'Mid Bucket',
    high_bucket: 'High Bucket',
  };
  return bucketNames[bucketPrefix] || bucketPrefix;
}

/**
 * Clamp a value between min and max
 */
export function clampValue(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Clamp calibration value to valid range (-100 to 100)
 * Used for calibration factors (can be negative)
 */
export function clampCalibrationValue(value: number): number {
  return clampValue(value, VALIDATION_LIMITS.MIN_VALUE, VALIDATION_LIMITS.MAX_VALUE);
}

/**
 * Clamp percentage value to valid range (0-100)
 * Used for Oil% and Water% in Retort History (cannot be negative)
 */
export function clampPercentage(value: number): number {
  return clampValue(value, PERCENTAGE_LIMITS.MIN, PERCENTAGE_LIMITS.MAX);
}

/**
 * Check if value is within calibration range (-100 to 100)
 * Used for calibration factors (can be negative)
 */
export function isCalibrationValueValid(value: number): boolean {
  return value >= VALIDATION_LIMITS.MIN_VALUE && value <= VALIDATION_LIMITS.MAX_VALUE;
}

/**
 * Check if percentage value is within valid range (0-100)
 * Used for Oil% and Water% in Retort History (cannot be negative)
 */
export function isPercentageValid(value: number): boolean {
  return value >= PERCENTAGE_LIMITS.MIN && value <= PERCENTAGE_LIMITS.MAX;
}

/**
 * Check if sum of two values exceeds maximum allowed sum
 */
export function isSumValid(value1: number, value2: number, maxSum: number = VALIDATION_LIMITS.MAX_SUM): boolean {
  return (value1 + value2) <= maxSum;
}

/**
 * Calculate calibration factors from predicted and retort values
 */
export function calculateCalibrationFactors(
  predictedOil: number,
  retortOil: number,
  predictedWater: number,
  retortWater: number
): { oilFactor: number; waterFactor: number } {
  return {
    oilFactor: predictedOil - retortOil,
    waterFactor: predictedWater - retortWater,
  };
}

/**
 * Handle API error and return appropriate error message
 */
export function getApiErrorMessage(error: unknown, defaultMessage: string): string {
  const apiError = asApiError(error);
  if (apiError.response?.data?.detail) {
    return apiError.response.data.detail;
  }
  if (apiError.message) {
    return apiError.message;
  }
  return defaultMessage;
}

/**
 * Check if error is unauthorized (401)
 */
export function isUnauthorizedError(error: unknown): boolean {
  return asApiError(error).response?.status === 401;
}

/**
 * Check if error is forbidden (403)
 */
export function isForbiddenError(error: unknown): boolean {
  return asApiError(error).response?.status === 403;
}

/**
 * Check if error is not found (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return asApiError(error).response?.status === 404;
}

/**
 * Get numeric value or default
 */
export function getNumericValue(value: number | '', defaultValue: number = 0): number {
  return typeof value === 'number' ? value : defaultValue;
}
